import { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import './careos-prototype.css';
import { PatientProfile } from './PatientProfile';
import { usePrototypeState } from '../PrototypeBar/PrototypeStateContext';

/**
 * Route wrapper for the careOS PatientProfile (`/provider-home/patient/:patientId`).
 *
 * The prototype journey / active-lab-order state it passes down comes from
 * `PrototypeStateContext` rather than local state: the controls that drive it
 * live in the workspace chrome at the top of the window (`PrototypeBar`, under
 * `GlobalBar`), and the profile both reads and writes it — e.g. LabsTab placing
 * an order sets `activeLabOrder`, which the bar's Labs dropdown then reflects.
 */
export function PatientProfileRoute() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // This route is reachable from two tabs. `?from=hc-dashboard` is set by the
  // Care roster so Back returns to the roster rather than dropping the user on
  // provider Home, which they may never have visited. A query param rather
  // than history state so it survives a refresh mid-demo, matching how the
  // roster already keeps its own filters in the URL.
  const cameFromRoster = searchParams.get('from') === 'hc-dashboard';
  const backTo = cameFromRoster ? '/hc-dashboard' : '/provider-home';
  const {
    journeyStage, setJourneyStage,
    activeLabOrder, setActiveLabOrder,
    appStatus, claimsState,
    setActiveTab,
  } = usePrototypeState();

  // Leaving the profile clears the reported tab, so the bar doesn't keep
  // offering patient-tab controls on the next route.
  useEffect(() => () => setActiveTab(null), [setActiveTab]);

  return (
    // minWidth: 0 matters here — this div is the grid item in AppLayout's
    // `min-content auto` content column. Without it, the grid track's
    // automatic minimum size falls back to this subtree's content width, so
    // wide tab content (e.g. Claims Insights cards) inflates the whole page
    // instead of the Patient Overview panel holding a fixed width.
    <div className="careos-root" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, minWidth: 0 }}>
      <PatientProfile
        patientId={patientId ?? ''}
        onBack={() => navigate(backTo)}
        onTabChange={setActiveTab}
        journeyStage={journeyStage}
        setJourneyStage={setJourneyStage}
        activeLabOrder={activeLabOrder}
        setActiveLabOrder={setActiveLabOrder}
        appStatus={appStatus}
        claimsState={claimsState}
      />
    </div>
  );
}
